<script setup lang="ts">
  import { useAuthenticated, useHttpClient, useShared, useRoute } from '@othercommerce/foundation';
  import { useOffersPermissions, useSettlementsPermissions } from '@storefront/storefront';
  import { computed, type PropType, type Ref } from 'vue';
  import type { Breadcrumb } from '@storefront/types';
  import { Features } from '@shared/features';
  import type { StorefrontShared } from '@storefront/resources';

  defineProps({
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: true },
    customer: { type: Object as PropType<any>, required: true },
  });

  interface Shared extends StorefrontShared {
    isComplaintEnabled: boolean;
  }

  const shared = useShared() as unknown as Ref<Shared>;

  const { canRequestOffers } = useOffersPermissions();
  const { canRequestSettlements } = useSettlementsPermissions();
  const isComplaintEnabled = computed(() => shared.value.isComplaintEnabled);

  const session = useAuthenticated();
  const http = useHttpClient();
  const route = useRoute();

  function actionLogout() {
    http.delete(route('web.account.session.destroy'));
  }
</script>
<template>
  <main id="main" class="container container-narrow account-panel">
    <div class="account-panel__content">
      <div class="account-panel__greeting">
        <span>{{ $t('SHOP.ACCOUNT.HEADER.HELLO') }}</span>
        <span class="account-panel__firstname">{{ session?.company || session?.name }}!</span>
      </div>
      <div class="account-panel__options">
        <SfTile interactive :href="$route('web.account.order.index')">
          <div class="account-panel__item">
            <SfIconBox aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_ORDERS') }}</span>
          </div>
        </SfTile>
        <template v-if="isComplaintEnabled">
          <SfTile interactive :href="$route('web.account.product.complaint.index')">
            <div class="account-panel__item">
              <SfIconClipboardX aria-hidden="true" />
              <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_NEW_COMPLAINT') }}</span>
            </div>
          </SfTile>
          <SfTile interactive :href="$route('web.account.complaint.index')">
            <div class="account-panel__item">
              <SfIconPersonX aria-hidden="true" />
              <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_COMPLAINTS') }}</span>
            </div>
          </SfTile>
          <SfTile interactive :href="$route('web.account.product.refund.index')">
            <div class="account-panel__item">
              <SfIconArrowClockwise aria-hidden="true" />
              <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_NEW_REFUND') }}</span>
            </div>
          </SfTile>
          <SfTile interactive :href="$route('web.account.refund.index')">
            <div class="account-panel__item">
              <SfIconPersonArrowClockwise aria-hidden="true" />
              <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_REFUNDS') }}</span>
            </div>
          </SfTile>
        </template>
        <SfTile v-if="canRequestOffers" interactive :href="$route('web.account.offers.index')">
          <div class="account-panel__item">
            <SfIconEnvelopePaper aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_OFFERS') }}</span>
          </div>
        </SfTile>
        <SfTile v-if="canRequestSettlements" interactive :href="$route('web.account.settlements.index')">
          <div class="account-panel__item">
            <SfIconCoin aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_SETTLEMENTS') }}</span>
          </div>
        </SfTile>
        <SfTile v-if="canRequestSettlements" interactive :href="$route('web.account.warehouse.documents.index')">
          <div class="account-panel__item">
            <SfIconBox aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_WAREHOUSE_DOCUMENTS') }}</span>
          </div>
        </SfTile>
        <SfTile interactive :href="$route('web.account.edit')">
          <div class="account-panel__item">
            <SfIconPersonGear aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_ACCOUNT_DETAILS') }}</span>
          </div>
        </SfTile>
        <SfTile interactive :href="$route('web.account.notifications.edit')">
          <div class="account-panel__item">
            <SfIconBell aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_NOTIFICATIONS') }}</span>
          </div>
        </SfTile>
        <SfTile interactive :href="$route('web.account.review.index')">
          <div class="account-panel__item">
            <SfIconChat aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_REVIEWS') }}</span>
          </div>
        </SfTile>
        <SfTile interactive :href="$route('web.account.wishlist.index')">
          <div class="account-panel__item">
            <SfIconHeart aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_WISHLISTS') }}</span>
          </div>
        </SfTile>
        <SfTile interactive :href="$route('web.account.address.shipping.index')">
          <div class="account-panel__item">
            <SfIconGeo aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_SHIPPING_ADDRESSES') }}</span>
          </div>
        </SfTile>
        <SfTile interactive :href="$route('web.account.address.billing.index')">
          <div class="account-panel__item">
            <SfIconDocument aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_BILLING_ADDRESSES') }}</span>
          </div>
        </SfTile>
        <SfTile v-if="Features.loyaltyPoints.isEnabled" interactive :href="$route('web.account.loyalty.index')">
          <div class="account-panel__item">
            <SfIconCoin aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_LOYALTY_POINTS') }}</span>
          </div>
        </SfTile>
        <SfTile v-if="Features.affiliateMarketing.isEnabled" interactive :href="$route('web.account.affiliate.index')">
          <div class="account-panel__item">
            <SfIconShare aria-hidden="true" />
            <span class="account-panel__title">{{ $t('SHOP.ACCOUNT.LABEL.MENU_AFFILIATE_PROGRAM') }}</span>
          </div>
        </SfTile>
      </div>
      <div class="account-panel__logout">
        <SfButton transparent danger @click="actionLogout">
          <template #before>
            <SfIconPower aria-hidden="true" />
          </template>
          <template #default>
            {{ $t('SHOP.ACCOUNT.ACTION.SIGN_OUT') }}
          </template>
        </SfButton>
      </div>
    </div>
  </main>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-panel {
    &__content {
      margin: 60px 0;
    }

    &__greeting {
      font-size: 1.375rem;
    }

    &__firstname {
      font-weight: var(--font-weight-bold);
      margin-left: 0.375rem;
    }

    &__options {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 2.5rem;
      margin: 2.5rem 0;

      > .tile > .tile__content {
        justify-content: center;
      }

      @media(hover: hover) {
        a:hover {
          transform: translateY(-2px);
        }
      }
    }

    &__item {
      display: flex;
      flex-direction: column;
      align-items: center;
      row-gap: 1.25rem;
    }

    svg {
      font-size: 2rem;
    }

    &__title {
      margin-top: 0.625rem;
      font-size: 1.25rem;
      font-weight: var(--font-weight-semi);
      text-align: center;
    }

    &__logout {
      display: flex;
      justify-content: flex-end;
      font-size: 1.375rem;

      svg {
        font-size: 1.25rem;
      }
    }
  }

  @include media.query(lg) {
    .account-panel {
      --sf-tile-border-radius: var(--border-radius);

      &__content {
        margin: 0 auto 3.125rem;
      }

      &__options {
        grid-template-columns: 1fr 1fr;
        gap: 1.25rem;
        margin: 1.25rem 0;
        padding: 0 1rem;
      }

      &__item {
        gap: 0.75rem;

        svg {
          font-size: 1.75rem;
        }

        .tile__content {
          padding: 1.5rem 0.625rem;
          height: 100%;
        }
      }

      &__title {
        font-size: 0.875rem;
        margin: 0;
      }

      &__logout {
        padding: 0 1.25rem;

        .btn {
          font-size: 1rem;
          font-weight: var(--font-weight-regular);
        }

        svg {
          font-size: 1.125rem;
        }
      }

      &__greeting {
        background-color: var(--sf-account-color-white);
        padding: 2rem 1.25rem 0.875rem;
      }
    }
  }
</style>
